export interface Denomination {
  face: number;
  minor: number;
  kind: 'note' | 'coin';
}

interface CurrencyCash {
  notes: number[];
  coins: number[];
}

const CASH_BY_CURRENCY: Record<string, CurrencyCash> = {
  PKR: { notes: [5000, 1000, 500, 100, 75, 50, 20, 10], coins: [10, 5, 2, 1] },
  SAR: { notes: [500, 200, 100, 50, 20, 10, 5, 1], coins: [2, 1, 0.5, 0.25, 0.1, 0.05] },
  AED: { notes: [1000, 500, 200, 100, 50, 20, 10, 5], coins: [1, 0.5, 0.25] },
  QAR: { notes: [500, 200, 100, 50, 10, 5, 1], coins: [0.5, 0.25] },
  USD: { notes: [100, 50, 20, 10, 5, 2, 1], coins: [1, 0.25, 0.1, 0.05, 0.01] },
  GBP: { notes: [50, 20, 10, 5], coins: [2, 1, 0.5, 0.2, 0.1, 0.05, 0.02, 0.01] },
  EUR: { notes: [200, 100, 50, 20, 10, 5], coins: [2, 1, 0.5, 0.2, 0.1, 0.05, 0.02, 0.01] },
  COP: { notes: [100000, 50000, 20000, 10000, 5000, 2000], coins: [1000, 500, 200, 100, 50] },
};

function toMinor(face: number): number {
  return Math.round(face * 100);
}

export function denominationsFor(currency: string | null | undefined): Denomination[] {
  const cash = CASH_BY_CURRENCY[(currency ?? '').toUpperCase()];
  if (!cash) return [];
  return [
    ...cash.notes.map((face): Denomination => ({ face, minor: toMinor(face), kind: 'note' })),
    ...cash.coins.map((face): Denomination => ({ face, minor: toMinor(face), kind: 'coin' })),
  ];
}

export function denominationKey(denomination: Denomination): string {
  return `${denomination.kind}:${denomination.face}`;
}

export function countedMinor(denominations: Denomination[], counts: Record<string, string>, looseMinor: number): number {
  const fromCash = denominations.reduce((sum, denomination) => sum + denomination.minor * wholeCount(counts[denominationKey(denomination)]), 0);
  return fromCash + (Number.isFinite(looseMinor) && looseMinor > 0 ? looseMinor : 0);
}

export function countedDenominations(denominations: Denomination[], counts: Record<string, string>): Record<string, number> {
  const byFace: Record<string, number> = {};
  for (const denomination of denominations) {
    const count = wholeCount(counts[denominationKey(denomination)]);
    if (count <= 0) continue;
    const face = String(denomination.face);
    byFace[face] = (byFace[face] ?? 0) + count;
  }
  return byFace;
}

function wholeCount(text: string | undefined): number {
  const value = Number(text || 0);
  return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
}
